import _ from 'lodash';
import React, {FC, Key, useCallback} from 'react';
import {connect} from 'react-redux';

import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {
  makeGetAttachments,
  makeGetAuthorFromComment,
  makeGetCanAddReactions,
  makeGetComment,
  makeGetConversation,
  makeGetHasSameAuthorAsPrevious,
  makeGetHighlightAnnotations,
  makeGetIsEditing,
  makeGetIsFromUser,
  makeGetIsReferencingTheSameMessageAsPrevious,
  makeGetIsVisibleOnlyToUser,
  makeGetPinnedByTeammate,
  makeGetReactionsVisualProps,
  makeGetReferencedActivity,
  makeGetReferencedAuthor,
  makeGetReferencedComment,
  makeGetReferencedRecipient,
} from '../../../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {useAppDispatch} from '../../../../../helpers/reduxHelpers';
import {ActivityModel, CommentActivityModel, isActivityPinned} from '../../../../../models/api/activityModel';
import {AnnotationModel} from '../../../../../models/api/annotationModel';
import {ConversationActionContextsEnum} from '../../../../../models/api/conversationCommonTypes';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {deleteActivityTask} from '../../../../../services/tasks/deleteActivityTask';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../services/tracking';
import {AppState} from '../../../../../store/appState';
import {toggleReactionOnCommentAsyncAction} from '../../../../../store/data/comments/commentsReactionsActions';
import {highlightInCommentAction} from '../../../../../store/data/comments/highlightActions';
import {getCanEditComments} from '../../../../../store/data/company/companySelectors';
import {
  makeGetConversationInboxes,
  makeGetIsTeamConversation,
} from '../../../../../store/data/conversations/conversationsSelectors';
import {
  pinActivityAction,
  unpinActivityAction,
} from '../../../../../store/data/conversations/pinActivitiesToConversationsActions';
import {
  getAllAliases,
  getMentionableAliases,
} from '../../../../../store/data/teammates/mentionTeammatesSelectors';
import {getUserAlias, getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {getHasQuoteComment} from '../../../../../store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {getPanels} from '../../../../../store/panels/panelsSelectors';
import {PanelTypesEnum, ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {expandActivityViewerAsyncAction} from '../../../../../store/panels/viewer/activity/activityViewerActions';
import {
  editCommentAction,
  jumpToActivityAction,
  referenceActivityAction,
} from '../../../../../store/panels/viewer/viewerPanelActions';
import {makeGetContentPage} from '../../../../../store/panels/viewer/viewerPanelSelectors';
import {getEmojiValidationMap} from '../../../../../store/stateless/emojiSelectors';
import {
  GetIsPersonalizeDemoInboxActiveSelector,
  makeGetIsPersonalizedDemoInboxActive,
} from '../../../../../store/stateless/guidedDemoSelectors';
import {TimelineVariationsEnum} from '../../../../../store/timelineVariations/timelineVariationSelectors';
import {demoInboxTeammateEmailPrefix} from '../../../../startGuide/startGuideCommon';
import {CommentViewer} from './commentViewer';

/*
 * Props.
 */

export interface CommentViewerContainerProps {
  panelId: ViewerPanelId;
  activity: CommentActivityModel;
  previousActivity: ActivityModel | undefined;
  conversationId: number;
  isLastSendingComment: boolean;
  isFirstComment: boolean;
  isNewUnread: boolean;
  isVisible: boolean;
  jumpKey?: Key;
  isHighlighted?: boolean;
  jumpSourceActivityId?: number;
  conversationActionContext: ConversationActionContextsEnum;
  cellsContext: CellsContext;
  canceledReminder?: ReminderModel;
  timelineVariation: TimelineVariationsEnum;
}

type CommentViewerStateProps = ResolveStateProps<typeof mapStateToProps>;

type CommentViewerContainerComponentProps = CommentViewerContainerProps & CommentViewerStateProps;

/*
 * Component.
 */

const CommentViewerContainerComponent: FC<CommentViewerContainerComponentProps> = (props) => {
  const {
    activity,
    allAliases,
    annotations,
    attachments,
    author,
    canAddReactions,
    canEditComments,
    comment,
    hasSameAuthorAsPrevious,
    isEditing,
    isFromUser,
    isLastSendingComment,
    isNewUnread,
    jumpKey,
    jumpSourceActivityId,
    isHighlighted,
    isVisible,
    mentionableAliases,
    panelId,
    previousActivity,
    reactions,
    referencedAuthor,
    referencedComment,
    referencedRecipient,
    userAlias,
    userId,
    shouldShowDemoInboxTip,
    isVisibleOnlyToUser,
    hasPopupDiscussion,
    pinnedByTeammate,
    emojiValidationMap,
    isReferencingTheSameMessageAsPrevious,
    timelineVariation,
  } = props;
  const {conversationId, commentUid} = activity;

  const dispatch = useAppDispatch();

  const onToggleReaction = useCallback(
    (shortcode: string) => {
      if (!canAddReactions) {
        return;
      }

      dispatch(toggleReactionOnCommentAsyncAction(commentUid, shortcode));
    },
    [dispatch, canAddReactions, commentUid],
  );

  const onAddReaction = useCallback(
    (shortcode: string) => {
      if (!canAddReactions) {
        return;
      }

      // TODO: Add error handling.
      dispatch(toggleReactionOnCommentAsyncAction(commentUid, shortcode, true));
    },
    [dispatch, canAddReactions, commentUid],
  );

  const onReferencedActivityClick = useCallback(
    (activityId: number) => {
      dispatch(jumpToActivityAction(panelId, {id: activityId}, activity.id));
      dispatch(expandActivityViewerAsyncAction(panelId, activityId));
    },
    [dispatch, activity.id, panelId],
  );

  const editComment = useCallback(() => {
    dispatch(editCommentAction(panelId, commentUid));
  }, [dispatch, panelId, commentUid]);

  const deleteComment = useCallback(() => dispatch(deleteActivityTask(activity.id)), [activity, dispatch]);

  const onReplyClick = useCallback(() => {
    dispatch(referenceActivityAction(conversationId, activity.id));
  }, [dispatch, conversationId, activity]);

  const togglePinComment = useCallback(() => {
    const isPinned = isActivityPinned(activity);

    trackInterfaceEvent(InterfaceEventsEnum.P3_COMMENT_PIN_ICON_CLICK, {
      conversation_id: activity.conversationId,
      activity_id: activity.id,
      has_attachment: attachments.length > 0,
      action: isPinned ? 'unpin' : 'pin',
    });

    return dispatch(
      isPinned
        ? unpinActivityAction(activity.id, activity.conversationId)
        : pinActivityAction(activity.id, activity.conversationId),
    );
  }, [dispatch, activity, attachments]);

  const onQuoteInCommentClick = useCallback(
    (annotation: AnnotationModel) => {
      if (!conversationId || !comment || !activity.id) {
        return;
      }

      dispatch(highlightInCommentAction(comment, annotation, activity.id));
    },
    [conversationId, activity.id, dispatch, comment],
  );

  if (!comment || !author) {
    return null;
  }

  return (
    <CommentViewer
      author={author}
      allAliases={allAliases}
      annotations={annotations}
      attachments={attachments}
      comment={comment}
      canAddReactions={canAddReactions}
      onReferencedActivityClick={onReferencedActivityClick}
      editComment={canEditComments ? editComment : undefined}
      deleteComment={canEditComments ? deleteComment : undefined}
      hasSameAuthorAsPrevious={hasSameAuthorAsPrevious}
      isReferencingTheSameMessageAsPrevious={isReferencingTheSameMessageAsPrevious}
      isFromUser={isFromUser}
      isLastSendingComment={isLastSendingComment}
      isNewUnread={isNewUnread}
      jumpKey={jumpKey}
      jumpSourceActivityId={jumpSourceActivityId}
      isHighlighted={isHighlighted}
      isVisible={isVisible}
      mentionableAliases={mentionableAliases}
      onAddReaction={onAddReaction}
      onQuoteInCommentClick={onQuoteInCommentClick}
      onReplyClick={onReplyClick}
      onToggleReaction={onToggleReaction}
      previousActivity={previousActivity}
      reactions={reactions}
      referencedAuthor={referencedAuthor}
      referencedComment={referencedComment}
      referencedRecipient={referencedRecipient}
      userAlias={userAlias}
      userId={userId}
      shouldShowDemoInboxTip={shouldShowDemoInboxTip}
      isVisibleOnlyToUser={isVisibleOnlyToUser && !hasPopupDiscussion}
      isPinned={isActivityPinned(activity)}
      pinnedByTeammate={pinnedByTeammate}
      togglePinComment={togglePinComment}
      panelId={panelId}
      isEditing={isEditing}
      emojiValidationMap={emojiValidationMap}
      namespace={props.namespace}
      hasQuoteComment={props.hasQuoteComment}
      isFirstComment={props.isFirstComment}
      conversationActionContext={props.conversationActionContext}
      cellsContext={props.cellsContext}
      conversationId={props.conversationId}
      canceledReminder={props.canceledReminder}
      timelineVariation={timelineVariation}
    />
  );
};

/*
 * Redux.
 */

function getPinnedByTeammateId(_state: AppState, props: CommentViewerContainerProps) {
  return props.activity.pinnedByTeammateId;
}

function getPanelId(_state: AppState, props: CommentViewerContainerProps) {
  return props.panelId;
}

function getActivityId(_state: AppState, props: CommentViewerContainerProps) {
  return props.activity.id;
}

type GetAuthorSelector = AppParametricSelector<any, TeammateModel | undefined>;
function makeGetShouldShowDemoInboxTip(
  getIsPersonalizedDemoInboxActive: GetIsPersonalizeDemoInboxActiveSelector,
  getAuthor: GetAuthorSelector,
) {
  return createSelector(
    [getIsPersonalizedDemoInboxActive, getAuthor],
    (isPersonalizedDemoInboxActive, author) => {
      if (!author) {
        return false;
      }
      // Show the demo inbox tip on this comment if: 1. the demo inbox is active and 2. the author of the comment is the demo teammate.
      return isPersonalizedDemoInboxActive && author.email.startsWith(demoInboxTeammateEmailPrefix);
    },
  );
}

/** Check that the same comment is not also visible in a popup */
function makeGetHasPopupDiscussion() {
  return createSelector([getPanelId, getPanels], (panelId, panels) => {
    const panel = panels[panelId];
    if (!panel || panel.type !== PanelTypesEnum.VIEWER) {
      return false;
    }

    // We are the popup here so no need to check popup discussion.
    if (panel.popupAppPanelId) {
      return false;
    }

    return _(panels).some((p) => Boolean(p?.popupAppPanelId));
  });
}

function makeGetNamespace(getConversation: ReturnType<typeof makeGetConversation>) {
  return createSelector([getConversation], (conversation) => conversation.namespace);
}

const mapStateToProps = makeMapStateToProps<CommentViewerContainerProps>()(() => {
  const getComment = makeGetComment();
  const getConversation = makeGetConversation();
  const getConversationInboxes = makeGetConversationInboxes(getConversation);
  const getIsTeamConversation = makeGetIsTeamConversation(getConversationInboxes);
  const getReferencedActivity = makeGetReferencedActivity(getComment);
  const getAuthor = makeGetAuthorFromComment(getComment);
  const getIsPersonalizedDemoInboxActive = makeGetIsPersonalizedDemoInboxActive(getConversation);
  const getPinnedByTeammate = makeGetPinnedByTeammate(getPinnedByTeammateId);
  const getContentPage = makeGetContentPage();
  const getNamespace = makeGetNamespace(getConversation);

  return {
    comment: getComment,
    userId: getUserId,
    author: getAuthor,
    attachments: makeGetAttachments(getComment),
    hasSameAuthorAsPrevious: makeGetHasSameAuthorAsPrevious(getComment),
    isReferencingTheSameMessageAsPrevious: makeGetIsReferencingTheSameMessageAsPrevious(getComment),
    isFromUser: makeGetIsFromUser(getComment),
    mentionableAliases: getMentionableAliases,
    reactions: makeGetReactionsVisualProps(getComment),
    canAddReactions: makeGetCanAddReactions(getConversation),
    userAlias: getUserAlias,
    allAliases: getAllAliases,
    canEditComments: getCanEditComments,
    referencedAuthor: makeGetReferencedAuthor(getReferencedActivity),
    referencedComment: makeGetReferencedComment(getReferencedActivity),
    referencedRecipient: makeGetReferencedRecipient(getReferencedActivity),
    shouldShowDemoInboxTip: makeGetShouldShowDemoInboxTip(getIsPersonalizedDemoInboxActive, getAuthor),
    isVisibleOnlyToUser: makeGetIsVisibleOnlyToUser(getConversation, getIsTeamConversation),
    hasPopupDiscussion: makeGetHasPopupDiscussion(),
    pinnedByTeammate: getPinnedByTeammate,
    isEditing: makeGetIsEditing(getComment, getContentPage),
    emojiValidationMap: getEmojiValidationMap,
    namespace: getNamespace,
    hasQuoteComment: getHasQuoteComment,
    annotations: makeGetHighlightAnnotations(getActivityId),
  };
});

export const CommentViewerContainer = connect(mapStateToProps)(CommentViewerContainerComponent);
